import { useEffect, useMemo, useRef, useState } from 'react'

import { getOrganizationMemberships, getUserGroups } from 'features/memberships_picker'
import { filterExcludedSuggestions } from 'features/recaps/modals/share_modal/utils'
import { useDebounce } from 'hooks/useDebounce'

const SEARCH_RESULT_LIMIT = 20
const SUGGESTED_LIMIT = 10
const SUGGESTED_GROUP_LIMIT = 2
const DEBOUNCE_MS = 300

const EMPTY_EXCLUSION = {
  excludedChipIds: new Set(),
  excludedUserUuids: new Set(),
  excludedGroupUuids: new Set(),
}

const userGroupToSuggestion = (userGroup) => ({
  id: `user_group-${userGroup.id}`,
  type: 'user_group',
  entity: userGroup,
})

const membershipToSuggestion = (membership) => ({
  id: `organization_membership-${membership.id}`,
  type: 'organization_membership',
  entity: membership,
})

const byName = (a, b) => (a.entity.name || '').localeCompare(b.entity.name || '')

/**
 * Fetches in-organization user groups + users for the recap recipients picker.
 * Groups are listed before users. The suggested (empty-query) state shows up to
 * 2 groups then fills with users (max 10 total); search shows up to 20 results.
 * Already-selected or already-granted recipients are excluded.
 */
export const useRecipientSuggestions = (query, suggestionExclusion = EMPTY_EXCLUSION) => {
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS).trim()
  const [groups, setGroups] = useState([])
  const [users, setUsers] = useState([])
  const [isSearching, setIsSearching] = useState(true)
  const requestIdRef = useRef(0)
  const suggestedSnapshotRef = useRef(null)
  const suggestedAmount = 15

  useEffect(() => {
    if (!debouncedQuery && suggestedSnapshotRef.current) {
      setGroups(suggestedSnapshotRef.current.groups)
      setUsers(suggestedSnapshotRef.current.users)
      setIsSearching(false)
      return undefined
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setIsSearching(true)

    const groupsRequest = getUserGroups(debouncedQuery, undefined, undefined, undefined, undefined, suggestedAmount)

    Promise.all([
      groupsRequest,
      getOrganizationMemberships(debouncedQuery, null, false, false, suggestedAmount),
    ])
      .then(([[groupsSuccess, groupsData], [usersSuccess, usersData]]) => {
        if (requestId !== requestIdRef.current) return

        if (!groupsSuccess) {
          // when user is not allowed to see any groups, it will be a 403, so expected
          setGroups([])
        }
        if (!usersSuccess) {
          setUsers([])
          setIsSearching(false)
          return
        }

        const nextGroups = groupsData?.collection ?? []
        const nextUsers = usersData?.results ?? []

        setGroups(nextGroups)
        setUsers(nextUsers)
        setIsSearching(false)

        if (!debouncedQuery) {
          suggestedSnapshotRef.current = { groups: nextGroups, users: nextUsers }
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return

        setGroups([])
        setUsers([])
        setIsSearching(false)
      })

    return undefined
  }, [debouncedQuery])

  const suggestions = useMemo(() => {
    const groupSuggestions = groups
      .map(userGroupToSuggestion)
      .sort(byName)
    const userSuggestions = users
      .map(membershipToSuggestion)
      .sort(byName)

    const filteredGroups = filterExcludedSuggestions(groupSuggestions, suggestionExclusion)
    const filteredUsers = filterExcludedSuggestions(userSuggestions, suggestionExclusion)

    if (!debouncedQuery) {
      const limitedGroups = filteredGroups.slice(0, SUGGESTED_GROUP_LIMIT)
      const remaining = SUGGESTED_LIMIT - limitedGroups.length
      return [...limitedGroups, ...filteredUsers.slice(0, remaining)]
    }

    return [...filteredGroups, ...filteredUsers].slice(0, SEARCH_RESULT_LIMIT)
  }, [groups, users, suggestionExclusion, debouncedQuery])

  return { suggestions, isSearching, hasQuery: Boolean(debouncedQuery) }
}
